/**
 * useStageMatrix
 *
 * 여러 Job의 스테이지를 한 번에 조회해 StageView 매트릭스(열=단계, 행=Job)를 구성.
 * - 각 Job마다 GET /jobs/{id}/stages 를 병렬 호출
 * - 열은 모든 Job이 공유하는 5단계 뼈대(첫 성공 응답)에서 추출
 * - 행 순서는 입력 jobs 순서 유지 (최신순 목록 그대로)
 *
 * Job 상세(단건)도 jobs에 1건만 넣어 재사용.
 */

import { useEffect, useState } from 'react';
import * as endpoints from '../api/endpoints';
import type { StageColumnGroup, StageRun } from '../organisms/StageView/StageView';
import {
  toStageColumns,
  toStageRun,
  deriveJobStatus,
  isJobSettled,
  mergeStageColumns,
  leafNosOf,
} from './stageMapping';
import type { StageJobRef } from './stageMapping';
import type { JobStatus } from '../types';

export interface StageMatrix {
  columns: StageColumnGroup[];
  runs: StageRun[];
  /** job id → 폴링으로 도출한 실시간 Job 상태. 배지/타임라인 연동용. */
  statuses: Record<number, JobStatus>;
  loading: boolean;
}

export function useStageMatrix(
  token: string | null,
  jobs: StageJobRef[]
): StageMatrix {
  const [columns, setColumns] = useState<StageColumnGroup[]>([]);
  const [runs, setRuns] = useState<StageRun[]>([]);
  const [statuses, setStatuses] = useState<Record<number, JobStatus>>({});
  const [loading, setLoading] = useState(false);

  // id+status 조합이 바뀔 때만 재조회 (진행 중 job의 상태 변화 반영).
  const key = jobs.map((j) => `${j.id}:${j.status}`).join(',');

  useEffect(() => {
    if (!token || jobs.length === 0) {
      setColumns([]);
      setRuns([]);
      setStatuses({});
      return;
    }
    let alive = true;
    let timer: ReturnType<typeof setInterval> | undefined;
    const stopPolling = () => {
      if (timer) {
        clearInterval(timer);
        timer = undefined;
      }
    };

    // 종결된 Job의 마지막 stages 결과 캐시(id→res)와 종결 id 집합.
    // 종결된 Job은 결과가 안 바뀌므로 fetchJobStages를 다시 부르지 않고 캐시로 렌더한다.
    type StagesResult = Awaited<ReturnType<typeof endpoints.fetchJobStages>>;
    const cache = new Map<number, StagesResult>();
    const settled = new Set<number>();

    // showLoading=true는 최초/키 변경 조회에만. 폴링은 조용히 갱신.
    const load = (showLoading: boolean) => {
      if (showLoading) setLoading(true);
      Promise.all(
        jobs.map((job) => {
          // 이미 종결된 Job은 재조회하지 않고 캐시된 결과를 그대로 사용.
          const cached = cache.get(job.id);
          if (cached && settled.has(job.id)) {
            return Promise.resolve({ job, res: cached });
          }
          return endpoints
            .fetchJobStages(job.id)
            .then((res) => ({ job, res }))
            // 실패 시 이전 캐시가 있으면 그것으로 유지, 없으면 제외.
            .catch(() => (cached ? { job, res: cached } : null));
        })
      ).then((results) => {
        if (!alive) return;
        const ok = results.filter(
          (r): r is { job: StageJobRef; res: StagesResult } => r !== null
        );
        // 캐시 갱신 + 종결 Job 표시(다음 폴링부터 재조회 스킵).
        // stages 도출 상태 또는 목록의 job.status가 완료/실패면 종결로 본다.
        for (const { job, res } of ok) {
          cache.set(job.id, res);
          if (
            isJobSettled(deriveJobStatus(res)) ||
            isJobSettled(job.status as JobStatus)
          ) {
            settled.add(job.id);
          }
        }
        // 열은 모든 Job의 컬럼을 no로 합집합(collector마다 자식 구성이 다름).
        // 셀은 이 공유 컬럼의 no 순서에 맞춰 배치해 칸 밀림을 막는다.
        const merged = mergeStageColumns(ok.map(({ res }) => toStageColumns(res)));
        const leafNos = leafNosOf(merged);
        const statusMap = Object.fromEntries(
          ok.map(({ job, res }) => [job.id, deriveJobStatus(res)])
        );
        setColumns(merged);
        setRuns(ok.map(({ job, res }) => toStageRun(res, job, leafNos)));
        setStatuses(statusMap);
        if (showLoading) setLoading(false);
        // 모든 Job이 종결됐으면 남은 fetchJobStages도 없으니 폴링 자체를 멈춘다.
        if (jobs.every((j) => settled.has(j.id))) stopPolling();
      });
    };

    load(true);
    // 10초 주기 폴링 — 진행 중 job의 단계가 실시간 반영되도록.
    timer = setInterval(() => load(false), 10_000);

    return () => {
      alive = false;
      stopPolling();
    };
    // key가 jobs의 신원을 대표하므로 jobs 대신 key에 의존.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, key]);

  return { columns, runs, statuses, loading };
}
